import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { 
  Plus, Pencil, Archive, Trash2, X, Save, Image as ImageIcon, 
  Video, Eye, EyeOff, Search, Upload, ExternalLink, Link, RefreshCw
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NewsPage = () => {
  const { getAuthHeaders } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [uploading, setUploading] = useState(false);
  const [imagePreviewKey, setImagePreviewKey] = useState(0);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    image: '',
    video_url: '',
    category: 'Notícia',
    source_name: '',
    source_url: '',
    youtube_video_id: '',
    published: true
  });

  const categories = ['Notícia', 'Produção', 'Marketing', 'Tendências', 'Legislação', 'Dicas', 'Mercado', 'Evento'];

  const fetchNews = async () => {
    try {
      const response = await axios.get(`${API}/news/all`, {
        headers: getAuthHeaders()
      });
      setNews(response.data);
    } catch (error) {
      console.error('Erro ao buscar notícias:', error);
    }
    setLoading(false);
  };

  const openNewModal = () => {
    setEditingNews(null);
    setFormData({
      title: '',
      excerpt: '',
      content: '',
      image: '',
      video_url: '',
      category: 'Notícia',
      source_name: '',
      source_url: '',
      youtube_video_id: '',
      published: true
    });
    setShowModal(true);
  };

  useEffect(() => {
    fetchNews();
    if (searchParams.get('action') === 'new') {
      openNewModal();
      setSearchParams({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openEditModal = (newsItem) => {
    setEditingNews(newsItem);
    setFormData({
      title: newsItem.title || '',
      excerpt: newsItem.excerpt || '',
      content: newsItem.content || '',
      image: newsItem.image || '',
      video_url: newsItem.video_url || '',
      category: newsItem.category || 'Notícia',
      source_name: newsItem.source_name || '',
      source_url: newsItem.source_url || '',
      youtube_video_id: newsItem.youtube_video_id || '',
      published: newsItem.published !== false
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingNews) {
        await axios.put(`${API}/news/${editingNews.id}`, formData, {
          headers: getAuthHeaders()
        });
      } else {
        await axios.post(`${API}/news`, formData, {
          headers: getAuthHeaders()
        });
      }
      
      // Fechar modal e limpar estado
      setShowModal(false);
      setEditingNews(null);
      
      // Buscar dados atualizados
      const response = await axios.get(`${API}/news/all`, {
        headers: getAuthHeaders()
      });
      setNews(response.data);
      
    } catch (error) {
      console.error('Erro ao salvar notícia:', error);
      alert('Erro ao salvar notícia');
    }
    setSaving(false);
  };

  const handleDelete = async (item) => {
    const confirmed = window.confirm(
      `Arquivar a matéria "${item.title}"?\n\nA notícia será removida do site público. Esta ação pode ser desfeita cadastrando a matéria novamente.`
    );
    if (!confirmed) return;

    try {
      await axios.delete(`${API}/news/${item.id}`, {
        headers: getAuthHeaders()
      });
      fetchNews();
    } catch (error) {
      console.error('Erro ao arquivar notícia:', error);
      alert('Erro ao arquivar notícia');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validar tipo de arquivo
    if (!file.type.startsWith('image/')) {
      alert('Por favor, selecione uma imagem válida (JPG, PNG, GIF, etc)');
      return;
    }

    // Validar tamanho (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('A imagem é muito grande. O tamanho máximo é 10MB.');
      return;
    }

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await axios.post(`${API}/upload/image`, formDataUpload, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });
      setFormData({ ...formData, image: `${BACKEND_URL}${response.data.url}` });
      setImagePreviewKey(prev => prev + 1);
    } catch (error) {
      console.error('Erro ao fazer upload:', error);
      if (error.response?.status === 401) {
        alert('Sessão expirada. Por favor, faça login novamente.');
      } else {
        alert('Erro ao fazer upload da imagem. Tente novamente.');
      }
    }
    setUploading(false);
    // Limpar o input para permitir upload do mesmo arquivo
    e.target.value = '';
  };

  const filteredNews = news.filter(item => 
    item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Notícias</h1>
          <p className="text-gray-400">Gerencie as notícias e matérias do site</p>
        </div>
        <button
          onClick={openNewModal}
          className="bg-gradient-to-r from-[#00F0FF] to-[#FF0099] text-white px-6 py-3 rounded-lg font-medium flex items-center gap-2 hover:opacity-90 transition-opacity"
        >
          <Plus size={20} />
          Nova Notícia
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" size={20} />
        <input
          type="text"
          placeholder="Buscar notícias..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg py-3 pl-12 pr-4 text-white placeholder-gray-500 focus:outline-none focus:border-[#00F0FF]"
        />
      </div>

      {/* News Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-[#00F0FF] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filteredNews.length === 0 ? (
        <div className="text-center py-16">
          <ImageIcon className="mx-auto mb-4 text-gray-600" size={48} />
          <p className="text-gray-400">Nenhuma notícia encontrada</p>
          <button
            onClick={openNewModal}
            className="mt-4 text-[#00F0FF] hover:underline"
          >
            Criar primeira notícia
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredNews.map((item) => (
            <div
              key={item.id}
              className="bg-[#0a0a0a] border border-white/5 rounded-xl overflow-hidden group hover:border-white/10 transition-all"
            >
              <div className="relative h-48">
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                <div className="absolute top-3 left-3 flex gap-2">
                  <span className="px-2 py-1 bg-[#FF0099] text-white text-xs font-bold rounded">
                    {item.category}
                  </span>
                  {!item.published && (
                    <span className="px-2 py-1 bg-yellow-500 text-black text-xs font-bold rounded">
                      Rascunho
                    </span>
                  )}
                </div>
                <div className="absolute top-3 right-3 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => openEditModal(item)}
                    className="p-2 bg-white/20 backdrop-blur rounded-lg hover:bg-white/30 transition-colors"
                    title="Editar matéria"
                    data-testid={`admin-edit-news-${item.id}`}
                  >
                    <Pencil size={16} className="text-white" />
                  </button>
                  <button
                    onClick={() => handleDelete(item)}
                    className="flex items-center gap-1.5 px-3 py-2 bg-amber-600/80 backdrop-blur rounded-lg hover:bg-amber-600 transition-colors"
                    title="Arquivar matéria (remove do site público)"
                    data-testid={`admin-archive-news-${item.id}`}
                  >
                    <Archive size={16} className="text-white" />
                    <span className="text-white text-xs font-semibold">Arquivar</span>
                  </button>
                </div>
              </div>
              <div className="p-4">
                <h3 className="text-white font-bold mb-2 line-clamp-2">{item.title}</h3>
                <p className="text-gray-400 text-sm line-clamp-2 mb-3">{item.excerpt}</p>
                {item.source_name && (
                  <p className="text-xs text-gray-500">Fonte: {item.source_name}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-start justify-center overflow-y-auto py-8">
          <div className="w-full max-w-3xl mx-4 bg-[#0a0a0a] rounded-2xl border border-white/10">
            {/* Modal Header */}
            <div className="p-6 border-b border-white/10 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">
                {editingNews ? 'Editar Notícia' : 'Nova Notícia'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Title */}
              <div>
                <label className="block text-gray-400 text-sm mb-2">Título *</label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full bg-[#111] border border-white/10 rounded-lg py-3 px-4 text-white focus:outline-none focus:border-[#00F0FF]"
                  placeholder="Título da notícia"
                />
              </div>

              {/* Excerpt */}
              <div>
                <label className="block text-gray-400 text-sm mb-2">Resumo *</label>
                <textarea
                  required
                  rows={2}
                  value={formData.excerpt}
                  onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                  className="w-full bg-[#111] border border-white/10 rounded-lg py-3 px-4 text-white focus:outline-none focus:border-[#00F0FF] resize-none"
                  placeholder="Breve resumo da notícia"
                />
              </div>

              {/* Content */}
              <div>
                <label className="block text-gray-400 text-sm mb-2">Conteúdo Completo</label>
                <textarea
                  rows={6}
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  className="w-full bg-[#111] border border-white/10 rounded-lg py-3 px-4 text-white focus:outline-none focus:border-[#00F0FF] resize-none"
                  placeholder="Conteúdo completo da matéria..."
                />
              </div>

              {/* Image */}
              <div>
                <label className="block text-gray-400 text-sm mb-2">
                  <span className="flex items-center gap-2">
                    <Link size={14} />
                    URL da Imagem * (Cole o link do Instagram, Google Drive, etc)
                  </span>
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    required
                    value={formData.image}
                    onChange={(e) => {
                      setFormData({ ...formData, image: e.target.value });
                      setImagePreviewKey(prev => prev + 1);
                    }}
                    className="flex-1 bg-[#111] border border-white/10 rounded-lg py-3 px-4 text-white focus:outline-none focus:border-[#00F0FF]"
                    placeholder="https://exemplo.com/imagem.jpg ou cole link do Instagram"
                  />
                  <button
                    type="button"
                    onClick={() => setImagePreviewKey(prev => prev + 1)}
                    className="px-3 py-3 bg-[#111] border border-white/10 rounded-lg text-[#00F0FF] hover:border-[#00F0FF] transition-colors"
                    title="Atualizar preview"
                  >
                    <RefreshCw size={20} />
                  </button>
                  {formData.image && (
                    <button
                      type="button"
                      onClick={() => {
                        setFormData({ ...formData, image: '' });
                        setImagePreviewKey(prev => prev + 1);
                      }}
                      className="px-3 py-3 bg-[#111] border border-white/10 rounded-lg text-red-500 hover:border-red-500 hover:bg-red-500/10 transition-colors"
                      title="Limpar imagem"
                    >
                      <Trash2 size={20} />
                    </button>
                  )}
                </div>
                
                {/* Opção de Upload */}
                <div className="mt-3 flex items-center gap-4">
                  <span className="text-gray-500 text-sm">ou</span>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageUpload}
                    accept="image/*"
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className={`flex items-center gap-2 px-4 py-2 bg-[#111] border border-dashed rounded-lg transition-colors ${
                      uploading 
                        ? 'border-[#00F0FF] text-[#00F0FF] cursor-wait' 
                        : 'border-white/20 text-gray-400 hover:border-[#00F0FF] hover:text-[#00F0FF]'
                    }`}
                  >
                    {uploading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-[#00F0FF] border-t-transparent rounded-full animate-spin" />
                        <span>Enviando imagem...</span>
                      </>
                    ) : (
                      <>
                        <Upload size={16} />
                        <span>Fazer upload do computador</span>
                      </>
                    )}
                  </button>
                </div>

                {/* Image Preview */}
                <div className="mt-4">
                  <p className="text-gray-500 text-xs mb-2">Preview da imagem:</p>
                  {formData.image ? (
                    <div className="relative inline-block">
                      <img 
                        key={imagePreviewKey}
                        src={formData.image} 
                        alt="Preview" 
                        className="h-32 w-auto rounded-lg object-cover border border-white/10 bg-[#111]"
                        style={{ minWidth: '128px' }}
                        onError={(e) => {
                          if (!e.target.dataset.retried) {
                            e.target.dataset.retried = 'true';
                            const url = new URL(formData.image, window.location.origin);
                            url.searchParams.set('t', Date.now());
                            e.target.src = url.toString();
                          } else {
                            e.target.parentElement.innerHTML = `
                              <div class="h-32 w-32 bg-[#1a1a1a] rounded-lg border border-red-500/30 flex flex-col items-center justify-center text-red-400 text-xs p-2">
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="mb-1">
                                  <circle cx="12" cy="12" r="10"/>
                                  <line x1="12" y1="8" x2="12" y2="12"/>
                                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                                </svg>
                                <span class="text-center">Erro ao carregar</span>
                              </div>
                            `;
                          }
                        }}
                        onLoad={(e) => {
                          e.target.style.opacity = '1';
                        }}
                      />
                    </div>
                  ) : (
                    <div className="h-32 w-32 bg-[#111] rounded-lg border border-dashed border-white/10 flex items-center justify-center text-gray-500 text-sm">
                      <div className="text-center">
                        <ImageIcon className="mx-auto mb-2" size={24} />
                        Cole uma URL
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Video URL */}
              <div>
                <label className="block text-gray-400 text-sm mb-2">
                  <span className="flex items-center gap-2">
                    <Video size={14} />
                    URL do Vídeo (YouTube, Instagram) - opcional
                  </span>
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={formData.video_url}
                    onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                    className="flex-1 bg-[#111] border border-white/10 rounded-lg py-3 px-4 text-white focus:outline-none focus:border-[#00F0FF]"
                    placeholder="https://youtube.com/watch?v=... ou link do Instagram"
                  />
                  {formData.video_url && (
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, video_url: '' })}
                      className="px-3 py-3 bg-[#111] border border-white/10 rounded-lg text-red-500 hover:border-red-500 hover:bg-red-500/10 transition-colors"
                      title="Limpar vídeo"
                    >
                      <Trash2 size={20} />
                    </button>
                  )}
                </div>
              </div>

              {/* Category */}
              <div>
                <label className="block text-gray-400 text-sm mb-2">Categoria *</label>
                <select
                  required
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full bg-[#111] border border-white/10 rounded-lg py-3 px-4 text-white focus:outline-none focus:border-[#00F0FF]"
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              {/* Source */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Nome da Fonte</label>
                  <input
                    type="text"
                    value={formData.source_name}
                    onChange={(e) => setFormData({ ...formData, source_name: e.target.value })}
                    className="w-full bg-[#111] border border-white/10 rounded-lg py-3 px-4 text-white focus:outline-none focus:border-[#00F0FF]"
                    placeholder="Ex: Billboard Brasil"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-2">URL da Fonte</label>
                  <input
                    type="url"
                    value={formData.source_url}
                    onChange={(e) => setFormData({ ...formData, source_url: e.target.value })}
                    className="w-full bg-[#111] border border-white/10 rounded-lg py-3 px-4 text-white focus:outline-none focus:border-[#00F0FF]"
                    placeholder="https://..."
                  />
                </div>
              </div>

              {/* Published */}
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, published: !formData.published })}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    formData.published ? 'bg-green-500' : 'bg-gray-600'
                  }`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                    formData.published ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
                <span className="text-gray-400">
                  {formData.published ? 'Publicado' : 'Rascunho'}
                </span>
              </div>

              {/* Actions */}
              <div className="flex gap-4 pt-4 border-t border-white/10">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingNews(null);
                  }}
                  className="flex-1 py-3 border border-white/10 rounded-lg text-gray-400 hover:text-white hover:border-white/30 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 py-3 bg-gradient-to-r from-[#00F0FF] to-[#FF0099] rounded-lg text-white font-medium flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50"
                >
                  {saving ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Save size={18} />
                      {editingNews ? 'Salvar Alterações' : 'Criar Notícia'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsPage;
